import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaperSizes } from '../../utils/paperSizeAPI';
import {
    Box, Card, CardContent, Typography, Stack, Button, Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress, MenuItem
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel, LocalPrintshop } from '@mui/icons-material';
import axios from 'axios';
import Navbar from '../setup/Navbar';
import {
    fetchTakeawayCounters,
    createTakeawayCounter,
    updateTakeawayCounter,
    updateTakeawayCounterStatus,
    deleteTakeawayCounter,
    reset
} from '../../features/setup/takeawayCounterSlice';

export default function TakeawayCounterManagement() {
    const dispatch = useDispatch();
    const { counters, isLoading, isError, isSuccess, message } = useSelector(state => state.takeawayCounters);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [testingPrinter, setTestingPrinter] = useState(false);
    const [paperSizes, setPaperSizes] = useState([
        { sizeID: 1, sizeName: '58mm Thermal', paperMm: 58, charsPerLine: 32 },
        { sizeID: 2, sizeName: '80mm Thermal', paperMm: 80, charsPerLine: 48 }
    ]);
    const [formData, setFormData] = useState({
        counterName: '',
        printerName: '',
        printerIP: '',
        printerPort: 9100,
        printerPaperMm: 80,
        printerStatus: 'active',
        status: 'active'
    });

    useEffect(() => {
        dispatch(fetchTakeawayCounters());
        loadPaperSizes();
    }, [dispatch]);

    const loadPaperSizes = async () => {
        try {
            const sizes = await fetchPaperSizes();
            setPaperSizes(sizes);
        } catch (error) {
            console.error('Error loading paper sizes:', error);
        }
    };

    useEffect(() => {
        if (isSuccess && message) {
            alert(message);
            dispatch(reset());
            setDialogOpen(false);
            dispatch(fetchTakeawayCounters());
        }
        if (isError && message) {
            alert(message);
            dispatch(reset());
        }
    }, [isSuccess, isError, message, dispatch]);

    const openDialog = (counter) => {
        if (counter) {
            setEditId(counter.takeawayCounterId);
            setFormData({
                counterName: counter.counterName,
                printerName: counter.printerName || '',
                printerIP: counter.printerIP || '',
                printerPort: counter.printerPort || 9100,
                printerPaperMm: counter.printerPaperMm || 80,
                printerStatus: counter.printerStatus || 'active',
                status: counter.status || 'active'
            });
        } else {
            setEditId(null);
            setFormData({
                counterName: '',
                printerName: '',
                printerIP: '',
                printerPort: 9100,
                printerPaperMm: 80,
                printerStatus: 'active',
                status: 'active'
            });
        }
        setDialogOpen(true);
    };

    const handleSave = () => {
        if (!formData.counterName) {
            alert('Counter name required');
            return;
        }
        if (editId) {
            dispatch(updateTakeawayCounter({ takeawayCounterId: editId, data: formData }));
        } else {
            dispatch(createTakeawayCounter(formData));
        }
    };

    const handleStatusToggle = (counter) => {
        const nextStatus = counter.status === 'active' ? 'inactive' : 'active';
        dispatch(updateTakeawayCounterStatus({ takeawayCounterId: counter.takeawayCounterId, status: nextStatus }));
    };

    const handleDelete = (takeawayCounterId) => {
        if (window.confirm('Delete this counter?')) {
            dispatch(deleteTakeawayCounter(takeawayCounterId));
        }
    };

    const handleTestPrinter = async () => {
        if (!formData.printerIP) {
            alert('Please enter Printer IP address');
            return;
        }
        setTestingPrinter(true);
        try {
            await axios.post(global.SET_API_URL + 'printerAssignment/testPrinter', {
                printerIP: formData.printerIP,
                printerPort: formData.printerPort,
                printerName: formData.printerName,
                categoryDesciption: `${formData.counterName} Takeaway Counter`
            });
            // alert('Test successful! Printer is reachable.');
        } catch (error) {
            alert('Printer test failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setTestingPrinter(false);
        }
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {isLoading && <LinearProgress />}
                <Card>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">Takeaway Counters</Typography>
                            <Button startIcon={<Add />} variant="contained" onClick={() => openDialog(null)}>Add Counter</Button>
                        </Stack>

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Printer</strong></TableCell>
                                    <TableCell><strong>IP</strong></TableCell>
                                    <TableCell><strong>Port</strong></TableCell>
                                    <TableCell><strong>Paper (mm)</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Printer Status</strong></TableCell>
                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {counters.map(c => (
                                    <TableRow key={c.takeawayCounterId}>
                                        <TableCell>{c.counterName}</TableCell>
                                        <TableCell>{c.printerName || '-'}</TableCell>
                                        <TableCell>{c.printerIP || '-'}</TableCell>
                                        <TableCell>{c.printerPort || '-'}</TableCell>
                                        <TableCell>{c.printerPaperMm || 80}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={c.status}
                                                color={c.status === 'active' ? 'success' : 'default'}
                                                size="small"
                                                icon={c.status === 'active' ? <CheckCircle /> : <Cancel />}
                                                onClick={() => handleStatusToggle(c)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={c.printerStatus || 'n/a'}
                                                color={c.printerStatus === 'active' ? 'success' : 'default'}
                                                size="small"
                                                icon={c.printerStatus === 'active' ? <CheckCircle /> : <Cancel />}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" size="small" onClick={() => openDialog(c)}><Edit /></IconButton>
                                            <IconButton color="error" size="small" onClick={() => handleDelete(c.takeawayCounterId)}><Delete /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{editId ? 'Edit Takeaway Counter' : 'Add Takeaway Counter'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Counter Name"
                                value={formData.counterName}
                                onChange={(e) => setFormData({ ...formData, counterName: e.target.value })}
                                required
                            />
                            <TextField
                                label="Printer Name"
                                value={formData.printerName}
                                onChange={(e) => setFormData({ ...formData, printerName: e.target.value })}
                            />
                            <TextField
                                label="Printer IP"
                                value={formData.printerIP}
                                onChange={(e) => setFormData({ ...formData, printerIP: e.target.value })}
                            />
                            <TextField
                                label="Printer Port"
                                type="number"
                                value={formData.printerPort}
                                onChange={(e) => setFormData({ ...formData, printerPort: Number(e.target.value) })}
                            />
                            <TextField
                                select
                                label="Printer Paper (mm)"
                                value={formData.printerPaperMm}
                                onChange={(e) => setFormData({ ...formData, printerPaperMm: Number(e.target.value) })}
                            >
                                {paperSizes.map((size) => (
                                    <MenuItem key={size.sizeID} value={size.paperMm}>
                                        {size.sizeName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            startIcon={<LocalPrintshop />}
                            onClick={handleTestPrinter}
                            disabled={testingPrinter}
                            variant="outlined"
                        >
                            {testingPrinter ? 'Testing...' : 'Test Printer'}
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
